/**
 * 数据库实例管理器
 * 负责数据库的创建、切换、关闭和删除
 */

import Dexie from 'dexie'
import { SPECIAL_CONVERSATIONS } from '@/common/constants/message'
import { generateConversationId } from '@/utils/chat'

/**
 * 聊天数据库类
 * 每个用户使用独立的数据库实例，数据库名称为：{prefix}-{userId}
 * 这样可以实现多用户数据隔离，退出登录时无需清除 IndexedDB
 */
export class ChatDatabase extends Dexie {
  constructor(dbName) {
    super(dbName)

    // 定义数据库架构
    // 版本 1: 初始架构
    this.version(1).stores({
      // 消息表
      // 主键: msg_id
      // 索引:
      //   - conversation_id: 查找会话中的所有消息
      //   - [conversation_id+seq_id]: 复合索引，用于分页
      //   - seq_id: 消息序列号，用于排序
      //   - created_at: 时间戳，用于排序
      messages: 'msg_id, conversation_id, [conversation_id+created_at], seq_id, created_at',

      // 会话表
      // 主键: conversation_id
      // 索引:
      //   - last_msg_at: 最后消息时间，用于排序
      //   - rank: 置顶标识，用于排序
      //   - target_uid: 对方用户ID，用于查找特定用户的会话
      //   - [rank+last_msg_at]: 复合索引，用于高效排序（置顶在前，最新消息在前）
      conversations: 'conversation_id, last_msg_at, rank, target_uid, is_stranger, [rank+last_msg_at]',

      // 同步游标表
      // 主键: id (固定为 'cursor')
      // 存储当前用户的同步状态
      //   - lastSeqId: 最大seq_id，用于增量同步消息
      //   - lastPage: 最后拉取的会话列表页码（-1表示未初始化）
      //   - lastSyncTime: 最后同步时间
      // sync_cursor: 'id, lastSyncTime'
    })
  }
}

/**
 * 生成数据库名称
 * @param {string} userId - 用户ID
 * @returns {string} 数据库名称
 */
export function createDatabaseName(userId) {
  return `${import.meta.env.VITE_DATABASE_PREFIX}-${userId}`
}
/**
 * 数据库实例管理器类
 * 维护当前用户的数据库实例
 */
class DatabaseManager {
  constructor() {
    this.currentDb = null
    this.currentUserId = null
  }

  /**
   * 初始化或切换用户的数据库
   * @param {string} userId - 用户ID（加密后的用户ID）
   * @param {string} memberId - 当前用户的会员ID
   * @returns {Promise<ChatDatabase>}
   */
  async initDatabase(userId, memberId) {
    if (!userId) {
      throw new Error('[DatabaseManager] userId is required')
    }

    // 如果是同一个用户，直接返回现有实例
    if (this.currentUserId === userId && this.currentDb) {
      return this.currentDb
    }

    // 切换了用户，关闭旧的数据库连接
    if (this.currentDb) {
      this.currentDb.close()
    }

    // 检查数据库是否存在
    const dbName = createDatabaseName(userId)
    const existingDatabases = await Dexie.getDatabaseNames()
    const isNewDatabase = !existingDatabases.includes(dbName)

    // 创建新的数据库实例
    this.currentDb = new ChatDatabase(dbName)
    this.currentUserId = userId

    console.log(`[DatabaseManager] Initialized database for user: ${userId}, isNew: ${isNewDatabase}`)

    // 如果是新数据库，插入特殊会话
    if (isNewDatabase) {
      await this.#initSpecialConversations(memberId)
    }

    return this.currentDb
  }

  /**
   * 初始化特殊会话（内部方法，仅在新数据库创建时调用）
   * @private
   * @param {string} memberId - 当前用户的会员ID
   */
  async #initSpecialConversations(memberId) {
    try {
      const db = this.currentDb
      if (!db) throw new Error('Database not initialized')

      // 构建特殊会话数据
      const now = new Date().getTime()
      const specialConversations = SPECIAL_CONVERSATIONS.map(config => {
        const { target_uid, target_nick_name, target_avatar_url, preview, rank, is_special } = config
        const conversationId = generateConversationId(memberId, config.member_id)
        return {
          conversation_id: conversationId,
          target_uid,
          target_nick_name,
          target_avatar_url,
          preview,
          rank,
          unreadCount: 0,
          conversation_type: 'Normal',
          created_at: now,
          last_msg_at: now,
          target_read_at: now,
          updated_at: now,
          is_special,
        }
      })

      // 批量插入
      await db.conversations.bulkAdd(specialConversations)
      console.log('[DatabaseManager] Special conversations initialized:', specialConversations.length)
    } catch (error) {
      console.error('[DatabaseManager] Failed to initialize special conversations:', error)
      // 不抛出错误，避免影响数据库初始化流程
    }
  }

  /**
   * 获取当前数据库实例
   * @returns {ChatDatabase|null}
   */
  getDatabase() {
    return this.currentDb
  }

  /**
   * 关闭当前数据库连接
   */
  closeDatabase() {
    if (this.currentDb) {
      this.currentDb.close()
      this.currentDb = null
      this.currentUserId = null
      console.log('[DatabaseManager] Database closed')
    }
  }

  /**
   * 删除指定用户的数据库
   * @param {string} userId - 用户ID
   */
  async deleteDatabase(userId) {
    const dbName = createDatabaseName(userId)
    await Dexie.delete(dbName)
    console.log(`[DatabaseManager] Deleted database: ${dbName}`)
  }
}

// 导出单例
export const dbManager = new DatabaseManager()
